import {useCallback, useMemo} from 'react';
import {VariantMapping, availableViewsConfigs} from '../common/config';
import type {ViewTypes} from '../common/types';
import {useEntry} from './use-entry';
import {useFeatureAppEnvHook} from './use-feature-app-env';

export const useViews = () => {
  const {config} = useFeatureAppEnvHook();
  if (!config) throw new Error(`Missing FeatureAppConfig`);

  const {featureServices} = useFeatureAppEnvHook();
  const historyService = featureServices[`s2:history`];
  const [entry, setEntry] = useEntry();

  const views = useMemo(() => VariantMapping[config.variant], [config.variant]);

  const getViewByName = useCallback((name: ViewTypes) => {
    return availableViewsConfigs[name];
  }, []);

  const setView = useCallback(
    <T extends React.JSXElementConstructor<any>>(
      view?: ViewTypes,
      props?: React.ComponentProps<T>,
    ) => {
      const searchParams = new URLSearchParams(
        historyService.rootHistory.location.search,
      );

      if (view) {
        searchParams.set(`view`, view);
      } else {
        searchParams.delete(`view`);
      }

      historyService.rootHistory.push(
        {
          ...historyService.rootHistory.location,
          search: searchParams.toString(),
        },
        props,
      );
    },
    [historyService],
  );

  const defaultView: ViewTypes = views[0];

  const restartJourney = useCallback(() => {
    // clears all sessionStorage
    setEntry(defaultView);
    // clears all search
    historyService.rootHistory.push({
      ...location,
      search: ``,
    });
    // goes to first View of variant with history Listener
  }, [views]);

  const entryView = useMemo(() => entry && getViewByName(entry), [entry]);

  const searchUnsubParams = new URLSearchParams(
    historyService.rootHistory.location.search,
  );

  const channelToken = searchUnsubParams.get(`channelToken`);
  const subscriptionID = searchUnsubParams.get(`subscriptionID`);

  return {
    getViewByName,
    setView,
    restartJourney,
    defaultView,
    views,
    entryView,
    channelToken,
    subscriptionID,
  };
};
